/**
 * @component ry-comments
 * @category  display
 * @summary   Threaded-comment container — the discussion wrapper that holds one or more `<ry-comment>` items (Fomantic UI `.ui.comments`). This is for THREADED comments (left-aligned, avatar-floated, indented replies), NOT chat bubbles — use `<ry-chat>` for conversational bubble UIs. Ported MIT from Fomantic UI — see LICENSE-THIRD-PARTY.md.
 *
 * Reference: Fomantic UI `comment` view (https://fomantic-ui.com/views/comment.html).
 * `<ry-comments>` is the rhythm/spacing scaffold; each `<ry-comment>` owns its
 * avatar / author / metadata / text / actions surface and may nest its own
 * `<ry-comment>` children as replies.
 *
 * @attribute minimal     boolean   default=false   Hides each comment's actions row until you hover the comment content (Fomantic `.ui.minimal.comments`). Without it, actions are always visible.
 * @attribute threaded    boolean   default=false   Draws a vertical connector line down the left edge of nested reply groups (Fomantic `.ui.threaded.comments`). Without it, replies are simply indented with no rule.
 *
 * @content   One or more `<ry-comment>` children. Any other HTML is permitted —
 *            the wrapper makes no assumptions and only applies layout to itself.
 *
 * @dom
 *   ry-comments[minimal?][threaded?]
 *     > ry-comment           (top-level comment)
 *     > ry-comment           (top-level comment)
 *       > ...                (its rendered content)
 *       > div.ry-comment__replies [data-ry-target="replies"]   (only when it has child <ry-comment>)
 *         > ry-comment       (nested reply)
 *
 * @gotcha    `<ry-comments>` sets no ARIA role — it is a plain container. Each
 *            `<ry-comment>` renders as an `<article>`-semantics wrapper so a
 *            screen reader treats each comment as a self-contained unit. If you
 *            need a live region for newly-arriving comments, wrap it yourself in
 *            `<div role="feed">` / `aria-live`.
 *
 * @gotcha    `minimal` and `threaded` are read off the CONTAINER, never the
 *            individual comment. A comment cannot opt itself into minimal/threaded
 *            independently — the whole thread is uniform (matches Fomantic).
 *
 * @example basic
 * ```html
 * <ry-comments>
 *   <ry-comment avatar="https://i.pravatar.cc/64?img=11" author="Matt" time="Today at 5:42PM">
 *     How artistic!
 *   </ry-comment>
 *   <ry-comment avatar="https://i.pravatar.cc/64?img=12" author="Elliot Fu" time="Yesterday at 12:30AM">
 *     This has been very useful for my research. Thanks as well!
 *   </ry-comment>
 * </ry-comments>
 * ```
 *
 * @example threaded-with-replies
 * ```html
 * <ry-comments threaded>
 *   <ry-comment avatar="https://i.pravatar.cc/64?img=12" author="Elliot Fu" time="Yesterday at 12:30AM">
 *     This has been very useful for my research. Thanks!
 *     <ry-comment avatar="https://i.pravatar.cc/64?img=15" author="Jenny Hess" time="Just now">
 *       Elliot you are always so right :)
 *     </ry-comment>
 *   </ry-comment>
 * </ry-comments>
 * ```
 *
 * @example minimal
 * ```html
 * <ry-comments minimal>
 *   <ry-comment avatar="https://i.pravatar.cc/64?img=11" author="Matt" time="Today at 5:42PM">
 *     Hover me to reveal the actions row.
 *     <div slot="actions"><a>Reply</a></div>
 *   </ry-comment>
 * </ry-comments>
 * ```
 *
 * @tutorial wraps-comments-without-transforming-them
 * ```js
 * const el = mount(`
 *   <ry-comments>
 *     <ry-comment author="A">one</ry-comment>
 *     <ry-comment author="B">two</ry-comment>
 *   </ry-comments>
 * `);
 * const items = el.querySelectorAll(':scope > ry-comment');
 * await expectEqual(items.length, 2, 'both top-level comments are direct children');
 * await expectEqual(el.tagName, 'RY-COMMENTS');
 * ```
 *
 * @tutorial minimal-and-threaded-are-plain-attribute-hooks
 * ```js
 * // Wrong assumption: minimal/threaded mutate the comment markup. They don't —
 * // they are pure attribute hooks on the container that CSS reads. The comment
 * // tree is identical with or without them.
 * const el = mount('<ry-comments minimal threaded><ry-comment author="A">x</ry-comment></ry-comments>');
 * await expectEqual(el.hasAttribute('minimal'), true);
 * await expectEqual(el.hasAttribute('threaded'), true);
 * ```
 *
 * @see ry-comment
 * @see ry-chat
 */

/**
 * @component ry-comment
 * @category  display
 * @summary   A single threaded comment — floated avatar on the left, then author + inline metadata, the comment text, and an actions row (Reply, Like, ...). Nest `<ry-comment>` children inside to author replies; they render indented under the parent. THREADED, left-aligned, no bubble tail — distinct from `<ry-chat-bubble>`. Ported MIT from Fomantic UI — see LICENSE-THIRD-PARTY.md.
 *
 * @attribute avatar     string?   Author avatar image URL. When set, renders a left-floated `<img>` with `alt` matching `author`. When absent, the content has no left indent.
 * @attribute author     string?   Author display name, rendered bold at the top of the content.
 * @attribute time       string?   Free-text metadata timestamp shown inline next to the author (e.g. "Today at 5:42PM"). Pre-formatted by the caller — the component does no date math.
 *
 * @content   The element's child nodes become the comment body, EXCEPT:
 *            (1) child `<ry-comment>` elements are lifted into a nested replies
 *            container, and (2) a `[slot="actions"]` / `[data-ry-target="actions"]`
 *            child becomes the actions row. Everything else is the comment text.
 *
 * @dom
 *   ry-comment[avatar?][author?][time?]   role="article"
 *     > div.ry-comment__avatar   [data-ry-target="avatar"]    (only when [avatar])
 *       > img src=avatar alt=author
 *     > div.ry-comment__content  [data-ry-target="content"]
 *       > span.ry-comment__author   [data-ry-target="author"]    (only when [author])
 *       > span.ry-comment__meta     [data-ry-target="metadata"]  (only when [time])
 *       > div.ry-comment__text      [data-ry-target="text"]      {comment body text/markup}
 *       > div.ry-comment__actions   [data-ry-target="actions"]   (only when an actions child was supplied)
 *     > div.ry-comment__replies  [data-ry-target="replies"]   (only when it has child <ry-comment>)
 *       > ry-comment × N   (nested replies, recursively rendered the same way)
 *
 * @gotcha    REPLIES ARE AUTHORED AS CHILD `<ry-comment>` ELEMENTS. Put a
 *            `<ry-comment>` inside another `<ry-comment>` and it is automatically
 *            moved into a `.ry-comment__replies` container and rendered indented.
 *            Nesting is arbitrary-depth — a reply can itself contain replies.
 *            This is the KEY feature that distinguishes ry-comment from ry-chat
 *            (chat bubbles are a flat list; comments are a tree).
 *
 * @gotcha    The actions row is OPT-IN. Supply it as a child with
 *            `slot="actions"` OR `data-ry-target="actions"` (e.g.
 *            `<div slot="actions"><a>Reply</a></div>`). Without such a child, no
 *            actions row renders — the component does NOT invent a default Reply
 *            link. The links inside are yours; the component only positions them.
 *
 * @gotcha    Setup separates children by KIND before rendering: nested
 *            `<ry-comment>` → replies, `[slot="actions"]` → actions, everything
 *            else → text. Because setup reads children once and rebuilds the
 *            subtree, mount the comment with all its children present. Setting
 *            `.innerHTML` on an already-upgraded empty `<ry-comment>` will NOT
 *            re-run setup (the archetype-B "setup-once-read-children" rule).
 *
 * @gotcha    Avatar `alt` text is the `author` attribute. Without `[author]`,
 *            alt is empty (decorative). Set `author` whenever a real photo shows.
 *
 * @example single
 * ```html
 * <ry-comment avatar="https://i.pravatar.cc/64?img=11" author="Matt" time="Today at 5:42PM">
 *   How artistic!
 * </ry-comment>
 * ```
 *
 * @example with-actions
 * ```html
 * <ry-comment avatar="https://i.pravatar.cc/64?img=12" author="Elliot Fu" time="Yesterday at 12:30AM">
 *   This has been very useful for my research. Thanks!
 *   <div slot="actions"><a>Reply</a> <a>Like</a></div>
 * </ry-comment>
 * ```
 *
 * @example nested-reply
 * ```html
 * <ry-comment avatar="https://i.pravatar.cc/64?img=12" author="Elliot Fu" time="Yesterday">
 *   Great write-up.
 *   <ry-comment avatar="https://i.pravatar.cc/64?img=15" author="Jenny Hess" time="Just now">
 *     Agreed!
 *   </ry-comment>
 * </ry-comment>
 * ```
 *
 * @example no-avatar
 * ```html
 * <ry-comment author="Anonymous" time="5 minutes ago">
 *   No avatar — the content sits flush against the left edge.
 * </ry-comment>
 * ```
 *
 * @tutorial renders-avatar-author-time-and-text
 * ```js
 * const el = mount('<ry-comment avatar="/m.jpg" author="Matt" time="Today at 5:42PM">How artistic!</ry-comment>');
 * const img = el.querySelector('[data-ry-target="avatar"] img');
 * await expectAttr(img, 'src', '/m.jpg');
 * await expectAttr(img, 'alt', 'Matt', 'avatar alt matches author');
 * await expectEqual(el.querySelector('[data-ry-target="author"]').textContent, 'Matt');
 * await expectEqual(el.querySelector('[data-ry-target="metadata"]').textContent, 'Today at 5:42PM');
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent.trim(), 'How artistic!');
 * ```
 *
 * @tutorial actions-slot-renders-into-actions-row
 * ```js
 * const el = mount('<ry-comment author="A">body<div slot="actions"><a>Reply</a></div></ry-comment>');
 * const actions = el.querySelector('[data-ry-target="actions"]');
 * await expectEqual(!!actions, true, 'actions row rendered from slot child');
 * await expectEqual(actions.querySelector('a').textContent, 'Reply');
 * // The text must NOT contain the actions markup.
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent.trim(), 'body');
 * ```
 *
 * @tutorial no-actions-child-means-no-actions-row
 * ```js
 * // Wrong assumption: every comment has a default Reply link. It does not —
 * // the actions row only renders when an actions child is supplied.
 * const el = mount('<ry-comment author="A">just text</ry-comment>');
 * await expectEqual(el.querySelector('[data-ry-target="actions"]'), null, 'no actions row');
 * ```
 *
 * @tutorial nested-comment-becomes-an-indented-reply
 * ```js
 * const el = mount(`
 *   <ry-comment author="Parent">parent text
 *     <ry-comment author="Child">child text</ry-comment>
 *   </ry-comment>
 * `);
 * const replies = el.querySelector('[data-ry-target="replies"]');
 * await expectEqual(!!replies, true, 'replies container rendered');
 * const child = replies.querySelector(':scope > ry-comment');
 * await expectEqual(!!child, true, 'child comment moved into replies container');
 * await expectEqual(child.querySelector('[data-ry-target="author"]').textContent, 'Child');
 * // Parent text is NOT polluted by the child comment text.
 * await expectEqual(el.querySelector(':scope > [data-ry-target="content"] > [data-ry-target="text"]').textContent.trim(), 'parent text');
 * ```
 *
 * @tutorial multi-level-nesting-is-preserved
 * ```js
 * // Wrong assumption: nesting is one level deep. It is arbitrary depth — a
 * // reply may itself contain replies.
 * const el = mount(`
 *   <ry-comment author="L0">a
 *     <ry-comment author="L1">b
 *       <ry-comment author="L2">c</ry-comment>
 *     </ry-comment>
 *   </ry-comment>
 * `);
 * const l1 = el.querySelector('[data-ry-target="replies"] > ry-comment');
 * await expectEqual(l1.querySelector('[data-ry-target="author"]').textContent, 'L1');
 * const l2 = l1.querySelector('[data-ry-target="replies"] > ry-comment');
 * await expectEqual(l2.querySelector('[data-ry-target="author"]').textContent, 'L2');
 * ```
 *
 * @tutorial no-avatar-no-avatar-slot
 * ```js
 * // Symmetric with the avatar tutorial: without [avatar] there is no avatar cell.
 * const el = mount('<ry-comment author="A">x</ry-comment>');
 * await expectEqual(el.querySelector('[data-ry-target="avatar"]'), null);
 * ```
 *
 * @tutorial article-role-for-a11y
 * ```js
 * const el = mount('<ry-comment author="A">x</ry-comment>');
 * await expectAttr(el, 'role', 'article', 'each comment is a self-contained article');
 * ```
 *
 * @see ry-comments
 * @see ry-chat-bubble
 */

import { RyElement } from '../core/ry-element.js';

export class RyComments extends RyElement {
  setup(): void {
    // Pure container — layout/visual handled by CSS via [minimal]/[threaded].
    // No markup transform; child <ry-comment> elements self-render.
  }
}

export class RyComment extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    // Setup-once guard: skip if already rendered (consumer built their own markup
    // or setup already ran).
    if (this.$(':scope > [data-ry-target="content"]')) return;

    if (!this.hasAttribute('role')) this.setAttribute('role', 'article');

    const avatar = this.getAttribute('avatar') ?? '';
    const author = this.getAttribute('author') ?? '';
    const time = this.getAttribute('time') ?? '';

    // Separate the direct children by kind:
    //   - nested <ry-comment>  → replies
    //   - [slot="actions"] / [data-ry-target="actions"] → actions row
    //   - everything else → comment body text
    const replyNodes: Element[] = [];
    let actionsNode: Element | null = null;
    const bodyNodes: Node[] = [];

    for (const node of Array.from(this.childNodes)) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        if (tag === 'ry-comment') {
          replyNodes.push(el);
          continue;
        }
        if (
          !actionsNode &&
          (el.getAttribute('slot') === 'actions' ||
            el.getAttribute('data-ry-target') === 'actions')
        ) {
          actionsNode = el;
          continue;
        }
      }
      bodyNodes.push(node);
    }

    // Build the body text HTML from the non-comment, non-actions nodes.
    const bodyHTML = bodyNodes
      .map((n) =>
        n.nodeType === Node.ELEMENT_NODE
          ? (n as Element).outerHTML
          : (n.textContent ?? '')
      )
      .join('')
      .trim();

    const avatarHTML = avatar
      ? `<div data-ry-target="avatar" class="ry-comment__avatar"><img src="${avatar}" alt="${author}"></div>`
      : '';

    const authorHTML = author
      ? `<span data-ry-target="author" class="ry-comment__author">${author}</span>`
      : '';

    const metadataHTML = time
      ? `<span data-ry-target="metadata" class="ry-comment__meta">${time}</span>`
      : '';

    // Actions: reuse the consumer's children, but normalize the wrapper so FORM
    // and THEME hooks always exist. Strip slot so it doesn't leak.
    const actionsInner = actionsNode ? actionsNode.innerHTML : '';
    const actionsHTML = actionsNode
      ? `<div data-ry-target="actions" class="ry-comment__actions">${actionsInner}</div>`
      : '';

    // Capture the reply elements' markup so they re-parse (and re-upgrade) inside
    // the replies container with their children present.
    const repliesInner = replyNodes.map((n) => n.outerHTML).join('');
    const repliesHTML = replyNodes.length
      ? `<div data-ry-target="replies" class="ry-comment__replies">${repliesInner}</div>`
      : '';

    this.classList.add('ry-comment');

    this.innerHTML = `
      ${avatarHTML}
      <div data-ry-target="content" class="ry-comment__content">
        ${authorHTML}
        ${metadataHTML}
        <div data-ry-target="text" class="ry-comment__text">${bodyHTML}</div>
        ${actionsHTML}
      </div>
      ${repliesHTML}
    `;
  }
}

customElements.define('ry-comments', RyComments);
customElements.define('ry-comment', RyComment);
